// components/drawers/DrawerArchivista.tsx
"use client";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";

interface DrawerArchivistaProps {
  onClose: () => void;
}

interface Module {
  nome: string;
  quiz_count: number;
  step_count: number;
  status: string;
  current_step: number;
  completion_percentage: number;
  adaptive_mode: string;
}

interface Progress {
  modules: Module[];
  pending_recalls: number;
  pending_daily_review: boolean;
  streak: { current: number; longest: number };
  quiz_seen_total: number;
  correct_total: number;
  study_days: number;
}

export default function DrawerArchivista({ onClose }: DrawerArchivistaProps) {
  const { user } = useAuth();
  const [progress, setProgress] = useState<Progress | null>(null);
  const [loading, setLoading] = useState(true);
  const loadControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (user?.id) loadProgress();
  }, [user?.id]);

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && user?.id) {
        loadProgress();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.id]);

  async function loadProgress() {
    if (loadControllerRef.current) loadControllerRef.current.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    setLoading(true);
    try {
      const [modulesRes, progressRes] = await Promise.all([
        fetch('/api/archivista/modules', { signal: controller.signal }),
        fetch('/api/archivista/progress', { signal: controller.signal }),
      ]);
      clearTimeout(timeoutId);
      if (controller.signal.aborted) return;

      const modulesData = await modulesRes.json();
      const progressData = await progressRes.json();

      setProgress({
        modules: modulesData.modules || [],
        pending_recalls: progressData.pending_recalls || 0,
        pending_daily_review: progressData.pending_daily_review || false,
        streak: progressData.streak || { current: 0, longest: 0 },
        quiz_seen_total: progressData.quiz_seen_total || 0,
        correct_total: progressData.correct_total || 0,
        study_days: progressData.study_days || 0,
      });
    } catch (error) {
      clearTimeout(timeoutId);
      if ((error as Error).name !== 'AbortError') {
        console.error('Errore caricamento progresso:', error);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  const totalQuiz = progress?.modules.reduce((sum, m) => sum + m.quiz_count, 0) || 0;
  const quizSeen = progress?.quiz_seen_total || 0;
  const correctTotal = progress?.correct_total || 0;
  const studyDays = progress?.study_days || 0;
  const overallProgress = totalQuiz > 0 ? Math.round((quizSeen / totalQuiz) * 100) : 0;
  const correctPercent = quizSeen > 0 ? Math.round((correctTotal / quizSeen) * 100) : 0;

  return (
    <>
      <div className="topbar">
        <button className="iconbtn" onClick={onClose}>✕</button>
        <div className="title">Studio</div>
        <button
          className="iconbtn"
          onClick={() => { onClose(); window.location.href = '/landing/razionale'; }}
          title="Perché 3940 funziona"
          style={{ fontSize: 16 }}
        >
          💡
        </button>
      </div>

      <div className="list" style={{ padding: 12 }}>
        {loading ? (
          <div className="helper">Caricamento...</div>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 16 }}>
              📋 Riepilogo Studio
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#3b82f6', marginBottom: 2 }}>
                  {quizSeen}/{totalQuiz}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6', marginBottom: 4 }}>
                  {overallProgress}%
                </div>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Quiz fatti</div>
              </div>

              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#22c55e', marginBottom: 2 }}>
                  {correctTotal}/{quizSeen}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#22c55e', marginBottom: 4 }}>
                  {correctPercent}%
                </div>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Risposte giuste</div>
              </div>

              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#FF6B35', marginBottom: 4 }}>
                  {Math.round(quizSeen * 1.5 / 60)}h
                </div>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Ore studiate</div>
              </div>

              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#8b5cf6', marginBottom: 4 }}>
                  {studyDays}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Giorni di studio</div>
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 12,
              padding: 16,
              color: 'white',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>🎯 Proiezione Completamento</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, opacity: 0.9 }}>Quiz rimanenti:</span>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{totalQuiz - quizSeen}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, opacity: 0.9 }}>Ore rimanenti:</span>
                <span style={{ fontSize: 12, fontWeight: 600 }}>~{Math.round((totalQuiz - quizSeen) * 1.5 / 60)}h</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, opacity: 0.9 }}>Giorni al ritmo attuale:</span>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  {studyDays > 0 && quizSeen > 0
                    ? `~${Math.round((totalQuiz - quizSeen) / (quizSeen / studyDays))} giorni`
                    : 'N/A'}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
